import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { subeOlustur, subeGuncelle } from '@/lib/services/sube-service'
import type { Sube, GpsKoordinati, Saat } from '@/types'

interface SubeFormModalProps {
  acik: boolean
  sube: Sube | null
  onKapat: () => void
  onKaydedildi: () => void
}

interface FormData {
  isim: string
  adres: string
  telefon: string
  gpsLat: string
  gpsLng: string
  yirmidortSaat: boolean
  saatBaslangic: string
  saatBitis: string
}

const BOS_FORM: FormData = {
  isim: '',
  adres: '',
  telefon: '',
  gpsLat: '',
  gpsLng: '',
  yirmidortSaat: true,
  saatBaslangic: '08:00',
  saatBitis: '22:00',
}

export function SubeFormModal({ acik, sube, onKapat, onKaydedildi }: SubeFormModalProps) {
  const [form, setForm] = useState<FormData>(BOS_FORM)
  const [hatalar, setHatalar] = useState<Partial<Record<keyof FormData, string>>>({})
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [konumYukleniyor, setKonumYukleniyor] = useState(false)

  useEffect(() => {
    if (sube) {
      setForm({
        isim: sube.isim,
        adres: sube.adres,
        telefon: sube.telefon ?? '',
        gpsLat: sube.konum.lat.toString(),
        gpsLng: sube.konum.lng.toString(),
        yirmidortSaat: sube.yirmidortSaat,
        saatBaslangic: sube.calismaSaatleri?.baslangic ?? '08:00',
        saatBitis: sube.calismaSaatleri?.bitis ?? '22:00',
      })
    } else {
      setForm(BOS_FORM)
    }
    setHatalar({})
    setHata(null)
  }, [sube, acik])

  function dogrula(): boolean {
    const e: Partial<Record<keyof FormData, string>> = {}

    if (!form.isim.trim()) e.isim = 'Şube adı zorunlu'
    if (!form.adres.trim()) e.adres = 'Adres zorunlu'

    const lat = parseFloat(form.gpsLat)
    const lng = parseFloat(form.gpsLng)
    if (isNaN(lat) || lat < -90 || lat > 90) e.gpsLat = 'Geçerli enlem girin (-90 ile 90 arası)'
    if (isNaN(lng) || lng < -180 || lng > 180) e.gpsLng = 'Geçerli boylam girin (-180 ile 180 arası)'

    if (!form.yirmidortSaat) {
      if (!form.saatBaslangic) e.saatBaslangic = 'Başlangıç saati zorunlu'
      if (!form.saatBitis) e.saatBitis = 'Bitiş saati zorunlu'
    }

    setHatalar(e)
    return Object.keys(e).length === 0
  }

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    if (!dogrula()) return

    setKaydediliyor(true)
    setHata(null)

    try {
      const konum: GpsKoordinati = {
        lat: parseFloat(form.gpsLat),
        lng: parseFloat(form.gpsLng),
      }

      // Firestore undefined kabul etmiyor — koşullu olarak ekle
      const data: Parameters<typeof subeOlustur>[0] = {
        isim: form.isim.trim(),
        adres: form.adres.trim(),
        konum,
        yirmidortSaat: form.yirmidortSaat,
      }

      const telefon = form.telefon.trim()
      if (telefon) {
        data.telefon = telefon
      }

      if (!form.yirmidortSaat) {
        data.calismaSaatleri = {
          baslangic: form.saatBaslangic as Saat,
          bitis: form.saatBitis as Saat,
        }
      }

      if (sube) {
        await subeGuncelle(sube.id, data)
      } else {
        await subeOlustur(data)
      }

      onKaydedildi()
    } catch (err) {
      console.error('Şube kaydedilemedi:', err)
      setHata('Kaydedilemedi: ' + (err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  function konumumuKullan() {
    if (!navigator.geolocation) {
      setHata('Tarayıcın konum servisini desteklemiyor.')
      return
    }
    setKonumYukleniyor(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({
          ...f,
          gpsLat: pos.coords.latitude.toFixed(6),
          gpsLng: pos.coords.longitude.toFixed(6),
        }))
        setKonumYukleniyor(false)
      },
      (err) => {
        setHata('Konum alınamadı: ' + err.message)
        setKonumYukleniyor(false)
      },
      { enableHighAccuracy: true, timeout: 15000 }
    )
  }

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={sube ? 'Şubeyi Düzenle' : 'Yeni Şube'}
      boyut="lg"
    >
      <form onSubmit={kaydet} className="space-y-4">
        <Field label="Şube Adı" hata={hatalar.isim} zorunlu>
          <input
            type="text"
            value={form.isim}
            onChange={e => setForm(f => ({ ...f, isim: e.target.value }))}
            className="input-base"
            placeholder="Örn: Kaşgar Süpermarket"
            disabled={kaydediliyor}
          />
        </Field>

        <Field label="Adres" hata={hatalar.adres} zorunlu>
          <textarea
            value={form.adres}
            onChange={e => setForm(f => ({ ...f, adres: e.target.value }))}
            className="input-base"
            rows={2}
            placeholder="Örn: Girne, Kaşgar bölgesi, ..."
            disabled={kaydediliyor}
          />
        </Field>

        <Field label="Telefon (opsiyonel)">
          <input
            type="tel"
            value={form.telefon}
            onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))}
            className="input-base"
            placeholder="+90 533 ..."
            disabled={kaydediliyor}
          />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              GPS Konumu <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={konumumuKullan}
              disabled={konumYukleniyor || kaydediliyor}
              className="text-sm text-brand-600 hover:underline disabled:opacity-50"
            >
              {konumYukleniyor ? 'Konum alınıyor...' : '📍 Şu an buradayım'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <input
                type="number"
                step="any"
                value={form.gpsLat}
                onChange={e => setForm(f => ({ ...f, gpsLat: e.target.value }))}
                className="input-base"
                placeholder="Enlem (35.34...)"
                disabled={kaydediliyor}
              />
              {hatalar.gpsLat && <p className="text-xs text-red-600 mt-1">{hatalar.gpsLat}</p>}
            </div>
            <div>
              <input
                type="number"
                step="any"
                value={form.gpsLng}
                onChange={e => setForm(f => ({ ...f, gpsLng: e.target.value }))}
                className="input-base"
                placeholder="Boylam (33.31...)"
                disabled={kaydediliyor}
              />
              {hatalar.gpsLng && <p className="text-xs text-red-600 mt-1">{hatalar.gpsLng}</p>}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            QR doğrulamada 50 metre yarıçap içinde olunmalı. Şubedeyken "Şu an buradayım" tuşuna basarsan kolay olur, ya da Google Maps'ten konuma sağ tık → koordinatları kopyala.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Çalışma Saatleri
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={form.yirmidortSaat}
                onChange={() => setForm(f => ({ ...f, yirmidortSaat: true }))}
                disabled={kaydediliyor}
              />
              <span className="text-sm">24 saat açık</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!form.yirmidortSaat}
                onChange={() => setForm(f => ({ ...f, yirmidortSaat: false }))}
                disabled={kaydediliyor}
              />
              <span className="text-sm">Belirli saatler</span>
            </label>
          </div>

          {!form.yirmidortSaat && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div>
                <label className="text-xs text-gray-600">Başlangıç</label>
                <input
                  type="time"
                  value={form.saatBaslangic}
                  onChange={e => setForm(f => ({ ...f, saatBaslangic: e.target.value }))}
                  className="input-base"
                  disabled={kaydediliyor}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Bitiş</label>
                <input
                  type="time"
                  value={form.saatBitis}
                  onChange={e => setForm(f => ({ ...f, saatBitis: e.target.value }))}
                  className="input-base"
                  disabled={kaydediliyor}
                />
              </div>
            </div>
          )}
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onKapat}
            className="btn-secondary flex-1"
            disabled={kaydediliyor}
          >
            İptal
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={kaydediliyor}>
            {kaydediliyor ? 'Kaydediliyor...' : sube ? 'Güncelle' : 'Ekle'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

interface FieldProps {
  label: string
  hata?: string
  zorunlu?: boolean
  children: React.ReactNode
}

function Field({ label, hata, zorunlu, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {zorunlu && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hata && <p className="text-xs text-red-600 mt-1">{hata}</p>}
    </div>
  )
}
